"use client";

import {
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  type ReactNode,
} from "react";
import Image from "next/image";
import { useRouter } from "next/navigation";
import { projects, type ProjectProps } from "../work/projectDetails";
import {
  palettes,
  usePortfolioTheme,
  usePortfolioThemeActions,
} from "./ThemeStudio";
import "./portfolio-console.css";
import LegacyTerminal from "./LegacyTerminal";
import TerminalBoot from "./TerminalBoot";
import FloatingConsoleWindow from "./FloatingConsoleWindow";
import NotebookDocument from "./NotebookDocument";
import PortfolioFiles, {type PortfolioFile} from "./PortfolioFiles";
import PortfolioGithubBrowser from "./PortfolioGithubBrowser";
import PortfolioPdfViewer from "./PortfolioPdfViewer";
import {githubHome} from "../../lib/github-scope";
type AuxApp = "files" | "pdf" | "browser";
type WindowApp = "terminal" | "workspace" | AuxApp;
import {
  ConsoleContext,
  usePortfolioConsole,
  type ConsoleScreen as Screen,
} from "./console-context";

type Place = { screen: Screen; query: string; project?: ProjectProps };
type Result = {
  id: string;
  title: string;
  detail: string;
  token: string;
  screen?: Screen;
  project?: ProjectProps;
  query?: string;
  action?: "copy" | "github" | "desk" | "text" | "files";
};
const email = "contact@sahiya.lk";
const resume = "/Sahindu-Gayanuka-Resume.pdf";
const commands: Result[] = [
  {id:"files", title:"Browse files", detail:"Home directory, documents & notebook", token:"files", action:"files"},
  {
    id: "projects",
    title: "Explore projects",
    detail: "Images, stacks & the story behind the work",
    token: "projects",
    screen: "projects",
  },
  {
    id: "about",
    title: "Meet Sahindu",
    detail: "Developer, security graduate & founder",
    token: "whoami",
    screen: "about",
  },
  {
    id: "theme",
    title: "Find your colour",
    detail: "Change the accent, right here",
    token: "theme",
    screen: "theme",
  },
  {
    id: "contact",
    title: "Start a conversation",
    detail: email,
    token: "contact",
    screen: "contact",
  },
  {
    id: "resume",
    title: "Read my résumé",
    detail: "Preview or download the PDF",
    token: "resume",
    screen: "resume",
  },
  {
    id: "copy",
    title: "Copy email address",
    detail: email,
    token: "copy email",
    action: "copy",
  },
  {
    id: "github",
    title: "Visit GitHub",
    detail: "Source code & experiments",
    token: "github",
    action: "github",
  },
  {
    id: "desk",
    title: "Visit the desk",
    detail: "A few things worth picking up",
    token: "desk",
    action: "desk",
  },
  {
    id: "coffee",
    title: "Take a coffee break",
    detail: "A small discovery from the desk",
    token: "coffee",
    screen: "coffee",
  },
  {
    id: "notebook",
    title: "Open the notebook",
    detail: "A sketch of how ideas become things",
    token: "notebook",
    screen: "notebook",
  },
  {
    id: "help",
    title: "What can I do here?",
    detail: "Commands, shortcuts & little discoveries",
    token: "help",
    screen: "help",
  },
  {
    id: "terminal",
    title: "Return to terminal",
    detail: "Return to the shell session",
    token: "terminal",
    action: "text",
  },
];
const names: Record<Screen, string> = {
  search: "home",
  projects: "projects",
  project: "case study",
  about: "whoami",
  contact: "contact",
  theme: "colour studio",
  resume: "résumé",
  help: "help",
  coffee: "coffee break",
  notebook: "notebook",
};

function matchingProjects(query: string) {
  const terms = query
    .toLowerCase()
    .replace(/^(skills?|projects?)\s*/, "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  return projects.filter((project) =>
    terms.every((term) =>
      [project.name, project.description, ...project.techNames]
        .join(" ")
        .toLowerCase()
        .includes(term)
    )
  );
}

export default function PortfolioConsole({
  children,
}: {
  children: ReactNode;
}) {
  const [request, setRequest] = useState<{ screen: Screen } | null>(null);
  const [coffeeLifted, setCoffeeLifted] = useState(false);
  const openConsole = useCallback(
    (screen: Screen = "search") => setRequest({ screen }),
    []
  );
  const toggleCoffee = useCallback(
    () => setCoffeeLifted((value) => !value),
    []
  );
  return (
    <ConsoleContext.Provider
      value={{ openConsole, coffeeLifted, toggleCoffee }}
    >
      {children}
      <ConsoleWindow request={request} />
    </ConsoleContext.Provider>
  );
}

function ConsoleWindow({ request }: { request: { screen: Screen } | null }) {
  const { openConsole, coffeeLifted, toggleCoffee } = usePortfolioConsole();
  const palette = usePortfolioTheme();
  const { choosePalette } = usePortfolioThemeActions();
  const router = useRouter();
  const id = useId().replace(/:/g, "");
  const input = useRef<HTMLInputElement>(null);
  const textInput = useRef<HTMLInputElement>(null);
  const resultsBox = useRef<HTMLDivElement>(null);
  const launcher = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const [sessionCreated, setSessionCreated] = useState(false);
  const [workspaceVisible, setWorkspaceVisible] = useState(false);
  const [front, setFront] = useState<WindowApp>("terminal");
  const [apps, setApps] = useState<Record<AuxApp, {running: boolean; visible: boolean}>>({
    files: {running:false, visible:false}, pdf: {running:false, visible:false}, browser: {running:false, visible:false},
  });
  const [fileTabs, setFileTabs] = useState<PortfolioFile[]>([]);
  const [selectedFile, setSelectedFile] = useState<PortfolioFile | null>(null);
  const [githubUrl, setGithubUrl] = useState(githubHome);
  const showApp = (app: AuxApp) => {
    setApps(previous => ({...previous, [app]:{running:true, visible:true}})); setFront(app);
  };
  const hideApp = (app: AuxApp, close = false) => {
    setApps(previous => ({...previous,[app]:{running:!close,visible:false}}));
    launcher.current?.focus({preventScroll:true});
  };
  const openFile = (file: PortfolioFile | null) => {
    if (file === "resume.pdf") {showApp("pdf");return;}
    if (file) setFileTabs(previous => previous.includes(file) ? previous : [...previous,file]);
    setSelectedFile(file);showApp("files");
  };
  const browseGithub = (url = githubHome) => {setGithubUrl(url);showApp("browser");};
  const [desktopInstalled, setDesktopInstalled] = useState(false);
  const [mode, setMode] = useState<"text" | "building" | "gui">("text");
  const [place, setPlace] = useState<Place>({ screen: "search", query: "" });
  const [trail, setTrail] = useState<Place[]>([]);
  const [highlight, setHighlight] = useState(0);
  const [history, setHistory] = useState<string[]>([]);
  const [notice, setNotice] = useState("");
  const { screen, query } = place;
  const normalized = query.toLowerCase().trim();

  const closeConsole = useCallback(() => {
    setOpen(false);
    if (workspaceVisible) {
      setFront("workspace");
      input.current?.focus({ preventScroll: true });
    } else launcher.current?.focus({ preventScroll: true });
  }, [workspaceVisible]);
  const showTerminal = useCallback(() => {
    setSessionCreated(true);
    setOpen(true);
    setFront("terminal");
    requestAnimationFrame(() => {
      const target = textInput.current ?? document.querySelector<HTMLButtonElement>(".floating-terminal .terminal-boot button");
      target?.focus({ preventScroll: true });
    });
  }, []);
  const minimizeWorkspace = () => {
    setWorkspaceVisible(false);
    setFront("terminal");
    if (open) textInput.current?.focus({ preventScroll: true });
    else launcher.current?.focus({ preventScroll: true });
  };
  const stopWorkspace = () => {
    setWorkspaceVisible(false);
    setMode("text");
    if (sessionCreated) showTerminal();
    else launcher.current?.focus({ preventScroll: true });
  };
  const stopTerminal = () => {
    setSessionCreated(false);
    if (mode === "building") setMode("text");
    closeConsole();
  };
  useEffect(() => {
    if (!request) return;
    showTerminal();
  }, [request, showTerminal]);

  useEffect(() => {
    const handleKey = (event: KeyboardEvent) => {
      if (event.defaultPrevented || event.isComposing || event.repeat) return;
      if (event.altKey && !event.ctrlKey && !event.metaKey &&
          (event.key === "Tab" || event.code === "Backquote" || event.key === "`")) {
        const running: WindowApp[] = [
          ...(sessionCreated ? ["terminal" as const] : []),
          ...(mode === "gui" ? ["workspace" as const] : []),
          ...(["files","pdf","browser"] as AuxApp[]).filter(app => apps[app].running),
        ];
        if (!running.length) return;
        event.preventDefault();
        const offset = event.shiftKey ? -1 : 1;
        const next = running[(Math.max(0,running.indexOf(front)) + offset + running.length) % running.length];
        if (next === "terminal") showTerminal();
        else if (next === "workspace") {
          setWorkspaceVisible(true);setFront("workspace");
          requestAnimationFrame(() => input.current?.focus({preventScroll:true}));
        } else {setApps(previous => ({...previous,[next]:{running:true,visible:true}}));setFront(next);}
        return;
      }
      const target = event.target instanceof Element ? event.target : null;
      const insideTerminal = !!target?.closest(".floating-terminal");
      const editing = target?.closest("input,textarea,select,[contenteditable='true']");
      const chord = (event.ctrlKey || event.metaKey) && event.key.toLowerCase() === "k";
      const slash = event.key === "/" && !event.ctrlKey && !event.metaKey && !event.altKey;
      if (chord) {
        event.preventDefault();
        if (insideTerminal && open) closeConsole();
        else openConsole();
      } else if (slash && !editing && !document.querySelector("dialog[open]")) {
        event.preventDefault(); openConsole();
      }
    };
    window.addEventListener("keydown", handleKey, true);
    return () => window.removeEventListener("keydown", handleKey, true);
  }, [open, openConsole, closeConsole, sessionCreated, mode, front, showTerminal, apps]);

  useEffect(() => {
    if (open && mode !== "building" && front === "terminal") textInput.current?.focus({ preventScroll: true });
    else if (workspaceVisible && front === "workspace") input.current?.focus({ preventScroll: true });
  }, [open, mode, workspaceVisible, request]);

  useEffect(() => {
    if (front === "files" || front === "pdf" || front === "browser") {
      if (apps[front].visible) requestAnimationFrame(() => document.querySelector<HTMLElement>(`.floating-${front} input, .floating-${front} .app-content`)?.focus({preventScroll:true}));
    }
  }, [apps, front]);

  const go = (next: Screen, nextQuery = "", project?: ProjectProps) => {
    setTrail((previous) => [...previous, place]);
    setPlace({ screen: next, query: nextQuery, project });
    setHighlight(0);
    setNotice("");
    input.current?.focus();
  };
  const back = () => {
    if (trail.length) {
      setPlace(trail[trail.length - 1]);
      setTrail((previous) => previous.slice(0, -1));
      setHighlight(0);
      input.current?.focus();
    } else if (screen !== "search") {
      setPlace({ screen: "search", query: "" });
      setHighlight(0);
      input.current?.focus();
    } else minimizeWorkspace();
    setNotice("");
  };
  const copyEmail = async () => {
    try {
      await navigator.clipboard.writeText(email);
      setNotice("Email copied. Ready when you are.");
    } catch {
      go("contact");
      setNotice("Clipboard unavailable. Select and copy the address below.");
    }
  };
  const visit = (hash: string) => {
    closeConsole();
    router.push("/#" + hash);
  };
  const execute = (result: Result) => {
    const command = query.trim() || result.token;
    setHistory((previous) =>
      [command, ...previous.filter((item) => item !== command)].slice(0, 8)
    );
    if (result.project) go("project", "", result.project);
    else if (result.action === "text") showTerminal();
    else if (result.action === "files") openFile(null);
    else if (result.action === "copy") void copyEmail();
    else if (result.action === "desk") visit("experience");
    else if (result.action === "github") {
      browseGithub();
    } else if (result.screen === "notebook") openFile("notebook.txt");
    else if (result.screen) go(result.screen, result.query);
  };

  const filteredProjects = matchingProjects(query);
  const resultList: Result[] =
    screen === "projects"
      ? filteredProjects.map((project) => ({
          id: "project-" + project.id,
          title: project.name,
          detail: project.techNames.slice(0, 3).join(" / "),
          token: "/" + String(project.id + 1).padStart(2, "0"),
          project,
        }))
      : [
          ...commands.filter(
            (command) =>
              !normalized ||
              (
                command.title +
                " " +
                command.token +
                " " +
                command.detail +
                (command.screen === "about" ? " about experience profile" : "")
              )
                .toLowerCase()
                .includes(normalized)
          ),
          ...(/^skills?\s+|^projects?\s+/.test(normalized)
            ? [
                {
                  id: "filtered-projects",
                  title:
                    "Projects using " +
                    normalized.replace(/^(skills?|projects?)\s+/, ""),
                  detail:
                    filteredProjects.length +
                    " matching projects · inspect the work",
                  token: "skills",
                  screen: "projects" as Screen,
                  query: normalized.replace(/^(skills?|projects?)\s+/, ""),
                },
              ]
            : []),
          ...(normalized
            ? filteredProjects.map((project) => ({
                id: "project-" + project.id,
                title: project.name,
                detail: project.techNames.slice(0, 3).join(" / "),
                token: "project",
                project,
              }))
            : []),
        ];
  const listed = screen === "search" || screen === "projects";
  const selected =
    resultList[Math.min(highlight, Math.max(0, resultList.length - 1))];
  const previewProject =
    screen === "project" ? place.project : selected?.project;

  useEffect(() => {
    if (!listed) return;
    resultsBox.current
      ?.querySelector<HTMLElement>("[aria-selected='true']")
      ?.scrollIntoView({ block: "nearest" });
  }, [highlight, query, listed]);

  const projectPreview = (project: ProjectProps) => (
    <div className="console-project-preview" key={project.id}>
      <Image
        src={project.image}
        alt={project.name + " interface preview"}
        width={720}
        height={420}
        className="console-project-image"
        sizes="(max-width: 650px) 85vw, 430px"
      />
      <div className="console-project-copy">
        <span className="console-eyebrow">
          PROJECT / {String(project.id + 1).padStart(2, "0")}
        </span>
        <h3>{project.name}</h3>
        <p>{project.description}</p>
        <div className="console-stack">
          {project.techNames.map((tech) => (
            <span key={tech}>{tech}</span>
          ))}
        </div>
        <a
          className="console-primary"
          href={"/projects/" + project.id}
          onClick={closeConsole}
        >
          Open case study <span>↗</span>
        </a>
      </div>
    </div>
  );

  return (
    <>
      <button
        ref={launcher}
        className="console-launch"
        aria-label="Open Sahindu console"
        aria-keyshortcuts="/ Control+k Meta+k"
        title="Open Sahindu console · / or Ctrl/Cmd + K"
        aria-haspopup="dialog"
        onClick={() => openConsole()}
      >
        <span aria-hidden="true">&gt;_</span>
        <kbd className="console-shortcut-hint" aria-hidden="true">
          /
        </kbd>
      </button>
      <FloatingConsoleWindow title="guest@sahiya.lk: ~" label="terminal"
        visible={open} front={front === "terminal"} onActivate={() => setFront("terminal")}
        onMinimize={closeConsole} onClose={stopTerminal} onEscape={closeConsole}>
          <div className="console-shell-panel" hidden={mode === "building"}>
            <LegacyTerminal
              inputRef={textInput}
              active={open && mode !== "building" && front === "terminal"}
              desktopInstalled={desktopInstalled}
              desktopRunning={mode === "gui"}
              request={request}
              onRender={() => {
                setDesktopInstalled(true);
                if (mode === "gui") {
                  setWorkspaceVisible(true);
                  setFront("workspace");
                  requestAnimationFrame(() => input.current?.focus({ preventScroll: true }));
                } else setMode("building");
              }}
              onClose={closeConsole}
              onVisit={visit}
              onProject={(project) => {
                closeConsole();
                router.push("/projects/" + project.id);
              }}
              onFile={openFile}
              onGithub={browseGithub}
              findProjects={matchingProjects}
            />
          </div>
          {mode === "building" ? (
            <TerminalBoot
              kind="desktop"
              onCancel={() => setMode("text")}
              onDone={() => {
                setPlace({ screen: "search", query: "" });
                setTrail([]);
                setMode("gui");
                setWorkspaceVisible(true);
                setFront("workspace");
              }}
            />
          ) : null}
      </FloatingConsoleWindow>
      <FloatingConsoleWindow title="SAHIYA / WORKSPACE" label="workspace" workspace
        visible={mode === "gui" && workspaceVisible} front={front === "workspace"}
        onActivate={() => setFront("workspace")} onMinimize={minimizeWorkspace}
        onClose={stopWorkspace} onEscape={back}>
          {mode === "gui" && <>
              <div className="console-breadcrumb">
                <button
                  onClick={() => {
                    setPlace({ screen: "search", query: "" });
                    setTrail([]);
                    setHighlight(0);
                    input.current?.focus();
                  }}
                >
                  ~
                </button>
                <span>/</span>
                <span>{names[screen]}</span>
                <button
                  className="console-text-toggle"
                  onClick={showTerminal}
                >
                  {" "}
                  &gt;_ Terminal
                </button>
                {screen !== "search" && (
                  <button className="console-back" onClick={back}>
                    ← Back
                  </button>
                )}
              </div>
              <div className="console-input-row">
                <span className="console-prompt" aria-hidden="true">
                  &gt;
                </span>
                <input
                  ref={input}
                  value={query}
                  aria-label={
                    listed
                      ? "Search projects or enter a command"
                      : "Enter another command"
                  }
                  role="combobox"
                  aria-autocomplete="list"
                  aria-expanded={listed && resultList.length > 0}
                  aria-controls={listed ? id + "-results" : undefined}
                  aria-activedescendant={
                    listed && selected ? id + "-" + selected.id : undefined
                  }
                  placeholder={
                    screen === "projects"
                      ? "Filter by name, stack or idea…"
                      : "An idea, a project, a little curiosity…"
                  }
                  spellCheck={false}
                  autoComplete="off"
                  onChange={(event) => {
                    if (!listed) {
                      setTrail((previous) => [...previous, place]);
                      setPlace({ screen: "search", query: event.target.value });
                    } else setPlace({ ...place, query: event.target.value });
                    setHighlight(0);
                    setNotice("");
                  }}
                  onKeyDown={(event) => {
                    if (event.key === "ArrowDown" && listed) {
                      event.preventDefault();
                      setHighlight((value) =>
                        Math.min(value + 1, resultList.length - 1)
                      );
                    }
                    if (event.key === "ArrowUp" && listed) {
                      event.preventDefault();
                      setHighlight((value) => Math.max(0, value - 1));
                    }
                    if (event.key === "Enter" && listed && selected) {
                      event.preventDefault();
                      execute(selected);
                    }
                  }}
                />
                <div className="console-input-tools">
                  {query && (
                    <button
                      aria-label="Clear search"
                      onClick={() => {
                        setPlace({ ...place, query: "" });
                        setHighlight(0);
                        input.current?.focus();
                      }}
                    >
                      ×
                    </button>
                  )}
                  <kbd>↵</kbd>
                </div>
              </div>
              <main
                className={"console-content console-screen-" + screen}
                key={screen + (place.project?.id ?? "")}
              >
                {listed ? (
                  <div className="console-browser">
                    <div className="console-result-column">
                      <div className="console-list-heading">
                        <span>
                          {screen === "projects"
                            ? "THE WORK"
                            : normalized
                            ? "MATCHES"
                            : "START SOMEWHERE"}
                        </span>
                        <span>
                          {resultList.length.toString().padStart(2, "0")}
                        </span>
                      </div>
                      <div
                        className="console-results"
                        id={id + "-results"}
                        role="listbox"
                        aria-label="Console results"
                        ref={resultsBox}
                      >
                        {resultList.map((result, index) => (
                          <button
                            role="option"
                            aria-selected={index === highlight}
                            id={id + "-" + result.id}
                            key={result.id}
                            tabIndex={-1}
                            onMouseEnter={() => setHighlight(index)}
                            onClick={() => execute(result)}
                          >
                            <span className="console-result-index">
                              {String(index + 1).padStart(2, "0")}
                            </span>
                            <span className="console-result-copy">
                              <strong>{result.title}</strong>
                              <small>{result.detail}</small>
                            </span>
                            <span
                              className="console-result-arrow"
                              aria-hidden="true"
                            >
                              ↗
                            </span>
                          </button>
                        ))}
                        {!resultList.length && (
                          <div className="console-empty">
                            <h3>No match. Try another angle.</h3>
                            <p>
                              Try “React”, “security”, “projects”, or “contact”.
                            </p>
                            <button
                              onClick={() => {
                                setPlace({ screen: "search", query: "" });
                                setHighlight(0);
                              }}
                            >
                              Show all commands ↗
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                    <aside
                      className="console-preview"
                      aria-label="Selected result preview"
                    >
                      {previewProject ? (
                        projectPreview(previewProject)
                      ) : (
                        <div className="console-welcome">
                          <span className="console-eyebrow">
                            {selected?.token || "HELLO, EXPLORER"}
                          </span>
                          <div className="console-mark" aria-hidden="true">
                            <span>&gt;</span>
                            <i>_</i>
                          </div>
                          <h3>{selected?.title || "Follow your curiosity."}</h3>
                          <p>
                            {selected?.detail ||
                              "A few commands. A closer look at the work. Some things you might not expect."}
                          </p>
                          <span className="console-preview-instruction">
                            Press Enter to explore <span>↵</span>
                          </span>
                        </div>
                      )}
                    </aside>
                  </div>
                ) : screen === "project" && previewProject ? (
                  projectPreview(previewProject)
                ) : screen === "about" ? (
                  <section className="console-editorial">
                    <span className="console-eyebrow">WHOAMI</span>
                    <h3>
                      Curious by default.
                      <br />
                      <em>Builder by choice.</em>
                    </h3>
                    <p>
                      I’m Sahindu Gayanuka, a full stack developer based in Sri
                      Lanka. I build across software, security, and the web, and
                      run my digital agency, AssembleX.
                    </p>
                    <dl className="console-facts">
                      <div>
                        <dt>Foundation</dt>
                        <dd>
                          BSc (Hons) Computer Security
                          <br />
                          Plymouth University · 2:1
                        </dd>
                      </div>
                      <div>
                        <dt>Experience</dt>
                        <dd>
                          TheDBot · Full Stack Developer
                          <br />
                          One year of professional experience
                        </dd>
                      </div>
                      <div>
                        <dt>Independent work</dt>
                        <dd>AssembleX · Founder & Developer</dd>
                      </div>
                    </dl>
                    <button
                      className="console-primary"
                      onClick={() => go("projects")}
                    >
                      See what I’ve built <span>↗</span>
                    </button>
                  </section>
                ) : screen === "contact" ? (
                  <section className="console-editorial">
                    <span className="console-eyebrow">
                      LET’S MAKE SOMETHING
                    </span>
                    <h3>
                      A good conversation
                      <br />
                      <em>is a good start.</em>
                    </h3>
                    <p>
                      Have a project, a collaboration, or a question in mind?
                    </p>
                    <a className="console-email" href={"mailto:" + email}>
                      {email}
                    </a>
                    <div className="console-actions">
                      <button
                        className="console-primary"
                        onClick={() => void copyEmail()}
                      >
                        Copy email <span>⧉</span>
                      </button>
                      <a
                        className="console-secondary"
                        href={
                          "mailto:" +
                          email +
                          "?subject=Let%E2%80%99s%20work%20together"
                        }
                      >
                        Compose an email ↗
                      </a>
                    </div>
                  </section>
                ) : screen === "theme" ? (
                  <section className="console-editorial">
                    <span className="console-eyebrow">
                      COLOUR STUDIO / LIVE
                    </span>
                    <h3>
                      Same personality.
                      <br />
                      <em>Your colour.</em>
                    </h3>
                    <p>
                      Choose an accent. The console, cube, and portfolio change
                      together.
                    </p>
                    <div
                      className="console-palettes"
                      role="group"
                      aria-label="Console accent colours"
                    >
                      {palettes.map((choice) => (
                        <button
                          key={choice.id}
                          onClick={() => choosePalette(choice)}
                          aria-pressed={palette.id === choice.id}
                        >
                          <span
                            style={{ backgroundColor: choice.accent }}
                            aria-hidden="true"
                          >
                            {palette.id === choice.id ? "✓" : ""}
                          </span>
                          {choice.name}
                        </button>
                      ))}
                    </div>
                    <p className="console-theme-status" aria-live="polite">
                      {palette.name} is on. Make yourself at home.
                    </p>
                  </section>
                ) : screen === "resume" ? (
                  <section className="console-editorial">
                    <span className="console-eyebrow">RÉSUMÉ / PDF</span>
                    <h3>
                      The longer story.
                      <br />
                      <em>Ready to take away.</em>
                    </h3>
                    <p>Sahindu Gayanuka · Experience, education, and skills.</p>
                    <div className="console-actions">
                      <a
                        className="console-primary"
                        href={resume}
                        onClick={event => {event.preventDefault();showApp("pdf");}}
                      >
                        Preview résumé <span>↗</span>
                      </a>
                      <a className="console-secondary" href={resume} download>
                        Download PDF ↓
                      </a>
                    </div>
                    <div className="console-document" aria-hidden="true">
                      <span>SG / RÉSUMÉ</span>
                      <strong>
                        Sahindu
                        <br />
                        Gayanuka.
                      </strong>
                      <i />
                      <i />
                      <i />
                      <small>FULL STACK DEVELOPER</small>
                    </div>
                  </section>
                ) : screen === "coffee" ? (
                  <section className="console-editorial console-coffee-page">
                    <span className="console-eyebrow">
                      NO DEADLINES IN HERE
                    </span>
                    <h3>
                      A little pause.
                      <br />
                      <em>Then back to building.</em>
                    </h3>
                    <button
                      className={
                        "console-coffee " + (coffeeLifted ? "is-lifted" : "")
                      }
                      aria-label={
                        coffeeLifted
                          ? "Set the coffee down"
                          : "Pick up the coffee"
                      }
                      aria-pressed={coffeeLifted}
                      onClick={toggleCoffee}
                    >
                      <span className="console-coffee-ring" />
                      <svg viewBox="0 0 180 160" fill="none" aria-hidden="true">
                        <g className="console-cup">
                          <path
                            d="M45 45h80v65c0 34-80 34-80 0Z"
                            fill="var(--console-accent)"
                          />
                          <ellipse
                            cx="85"
                            cy="45"
                            rx="40"
                            ry="17"
                            fill="color-mix(in srgb,var(--console-accent) 70%,#fff)"
                          />
                          <ellipse
                            cx="85"
                            cy="45"
                            rx="31"
                            ry="11"
                            fill="#3a2b23"
                          />
                          <path
                            d="M125 65c46-9 43 53 0 42"
                            stroke="var(--console-accent)"
                            strokeWidth="13"
                          />
                          <path
                            d="M74 23c-12-12 13-12 1-24m24 24c-12-12 13-12 1-24"
                            stroke="var(--console-muted)"
                            strokeWidth="2"
                          />
                        </g>
                      </svg>
                    </button>
                    <p>
                      {coffeeLifted
                        ? "Evidence of a well-used desk. The ring is there, too."
                        : "Pick it up. There’s a little evidence underneath."}
                    </p>
                  </section>
                ) : screen === "notebook" ? (
                  <NotebookDocument onProjects={() => go("projects")} />
                ) : (
                  <section className="console-editorial">
                    <span className="console-eyebrow">A SMALL FIELD GUIDE</span>
                    <h3>
                      No magic words.
                      <br />
                      <em>Just a little curiosity.</em>
                    </h3>
                    <p>
                      This console searches the portfolio locally. Try a
                      command, a project name, or a technology.
                    </p>
                    <div className="console-help">
                      {[
                        ["projects", "Browse the work, with previews."],
                        ["skills react", "Find projects that use React."],
                        ["whoami", "Meet the person behind the pixels."],
                        ["theme", "Change the accent across the site."],
                        ["contact", "Copy the address or compose an email."],
                        ["resume", "Preview or download my résumé."],
                        ["coffee", "Take a small break."],
                        ["notebook", "Have a look at a working sketch."],
                      ].map(([command, description]) => (
                        <button
                          key={command}
                          onClick={() => {
                            setTrail((previous) => [...previous, place]);
                            setPlace({ screen: "search", query: command });
                            setHighlight(0);
                            input.current?.focus();
                          }}
                        >
                          <code>{command}</code>
                          <span>{description}</span>
                          <span aria-hidden="true">↗</span>
                        </button>
                      ))}
                    </div>
                  </section>
                )}
              </main>
              <div className="console-feedback" role="status">
                {notice ||
                  (history.length ? (
                    <button
                      onClick={() => {
                        setPlace({ screen: "search", query: history[0] });
                        setHighlight(0);
                        input.current?.focus();
                      }}
                    >
                      ↑ Recall: {history[0]}
                    </button>
                  ) : (
                    <span>
                      Try{" "}
                      <button onClick={() => go("projects")}>projects</button>,{" "}
                      <button onClick={() => go("theme")}>theme</button> or{" "}
                      <button onClick={() => go("help")}>help</button>.
                    </span>
                  ))}
              </div>
              <footer className="console-footer">
                <span>
                  <kbd>↑</kbd>
                  <kbd>↓</kbd> choose <kbd>↵</kbd> explore
                </span>
                <span>
                  <kbd>esc</kbd> {trail.length || screen !== "search" ? "back" : "minimize"}
                </span>
                <span className="console-footer-note">
                  BUILT FOR THE CURIOUS.
                </span>
              </footer>
            </>}
      </FloatingConsoleWindow>
      {(["files","pdf","browser"] as AuxApp[]).map(app => <FloatingConsoleWindow key={app}
        app={app} title={app === "files" ? "FILES / HOME" : app === "pdf" ? "RÉSUMÉ / PDF" : "BROWSER / GITHUB"}
        label={app} workspace visible={apps[app].running && apps[app].visible} front={front===app}
        onActivate={() => setFront(app)} onMinimize={() => hideApp(app)} onClose={() => hideApp(app,true)}
        onEscape={() => hideApp(app)}>
        {apps[app].running && (app === "files" ? <PortfolioFiles tabs={fileTabs} selected={selectedFile}
          onSelect={openFile} onCloseTab={file => {setFileTabs(previous=>previous.filter(item=>item!==file));if(selectedFile===file)setSelectedFile(null);}}
          onPdf={() => showApp("pdf")} onProject={project=>{router.push("/projects/"+project.id);}} />
          : app === "pdf" ? <div className="app-content pdf-app" tabIndex={0}>
            <div className="app-toolbar"><button onClick={()=>openFile(null)}>▱ Files</button><span>Sahindu Gayanuka.pdf</span><a href={resume} download>Download ↓</a></div>
            <div className="file-tab-bar" role="tablist" aria-label="PDF documents"><button role="tab" aria-selected="true">Sahindu Gayanuka.pdf</button></div>
            <PortfolioPdfViewer url={resume} />
          </div> : <PortfolioGithubBrowser initialUrl={githubUrl} />)}
      </FloatingConsoleWindow>)}
      {(sessionCreated || mode === "gui" || mode === "building" || Object.values(apps).some(app=>app.running)) && <nav className={"console-session-dock"+(Object.values(apps).some(app=>app.running)?" has-document-apps":"")} aria-label="Running portfolio apps" title="Switch windows: Alt+Tab or Alt+`">
        {sessionCreated && <button data-console-dock="terminal" aria-label="Restore terminal" aria-pressed={open}
          onClick={open && front === "terminal" ? closeConsole : showTerminal}>
          <span aria-hidden="true">&gt;_</span><span className="dock-app-name">Terminal</span><i />
        </button>}
        {(mode === "gui" || mode === "building") && <button data-console-dock="workspace" aria-label="Restore workspace"
          aria-pressed={workspaceVisible} disabled={mode === "building"}
          onClick={() => {
            if (workspaceVisible && front === "workspace") minimizeWorkspace();
            else { setWorkspaceVisible(true); setFront("workspace"); requestAnimationFrame(() => input.current?.focus({ preventScroll: true })); }
          }}>
          <span aria-hidden="true">▧</span><span className="dock-app-name">{mode === "building" ? "Starting…" : "Workspace"}</span><i />
        </button>}
        {(["files","pdf","browser"] as AuxApp[]).filter(app=>apps[app].running).map(app=><button key={app}
          data-console-dock={app} aria-label={`Restore ${app}`} aria-pressed={apps[app].visible}
          onClick={()=>apps[app].visible && front===app ? hideApp(app) : showApp(app)}>
          <span aria-hidden="true">{app==="files"?"▱":app==="pdf"?"▤":"◎"}</span><span className="dock-app-name">{app==="files"?"Files":app==="pdf"?"PDF":"Browser"}</span><i />
        </button>)}
      </nav>}
    </>
  );
}
